import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { backofficeUrl } from 'src/shared/helpers';
import { EnvType } from 'src/shared/hooks';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { useLastUsedTool } from '../../../shared/hooks';
import AngleDown from 'src/shared/svg/angleDown';
import styled from 'styled-components';
import { usePageTransition } from 'src/shared/navigationTransition';
import {
    DropdownContent,
    DropdownItemBody,
    DropdownItemCaption,
    DropdownItemIcon,
    DropdownItemText,
    DropdownList,
    DropdownListItem,
    useDropdown
} from '../dropdown';
import { Icon } from '../icon';
import { AiTag, EarlyAccessTag, NewTag } from '../tag';
import { getTools } from '../toolbar/tools';

// Solution switcher moved from topbar. Sits at the top of the sidebar and
// lets the user jump between Visual Product Customizer, 3D Product
// Configurator, 3D DAM & AR Viewer, AI Agents Studio.

const Container = styled.div`
    position: relative;
    margin: 0 12px 10px;
`;

const Trigger = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    background: #f9f9f9;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    color: #333;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.07);
    }
`;

const TriggerIcon = styled(DropdownItemIcon).attrs({ $size: 'large' as const })`
    flex-shrink: 0;
`;

const TriggerLabel = styled.span`
    flex: 1;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AiGradientLabel = styled(TriggerLabel)`
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const AiGradientItemText = styled.span`
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const TriggerArrow = styled.span<{ $isOpen: boolean }>`
    display: inline-flex;
    transform: rotate(${(p) => (p.$isOpen ? '180deg' : '0deg')});
    transition: transform 0.2s ease;

    svg {
        width: 10px;
        height: 10px;
    }
`;

const SolutionSwitcher: FunctionComponent = () => {
    const { state } = useAppState();
    const { setLastUsedTool } = useLastUsedTool();
    const { userInfo, ecommerceSettings } = useAccountData();
    const navigate = useNavigate();
    const { startPageTransition } = usePageTransition();
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();

    const userID = userInfo?.userID;
    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: (userID || 0).toString()
    });

    const tools = getTools(isNewConfiguratorBackoffice);

    const getActiveToolType = (): string => {
        if (isNewConfiguratorBackoffice) {
            switch (state.lastUsedTool) {
                case 1:
                    return 'CUSTOMIZER';
                case 2:
                    return 'CONFIGURATOR';
                case 3:
                    return 'VIEWER';
                default:
                    return 'CUSTOMIZER';
            }
        }
        const url = window.location.href;
        if (url.indexOf('landings/configurator') !== -1 || url.indexOf('/Composer') !== -1) return 'CONFIGURATOR';
        if (url.indexOf('landings/viewer') !== -1 || url.indexOf('Admin/DAM') !== -1) return 'VIEWER';
        return 'CUSTOMIZER';
    };

    const activeToolType = getActiveToolType();
    const activeTool = tools.find((t) => t.type === activeToolType);
    const isWix = ecommerceSettings?.ecommerceName?.toUpperCase() === 'WIX';

    if (!userID) return null;

    const handleSelect = (tool: (typeof tools)[number]) => {
        closeDropdown();
        if (tool.type === 'AI_STUDIO') {
            startPageTransition(() => {
                navigate(backofficeUrl('/ai-creative-tools/studio'));
            });
            return;
        }
        if (tool.type === 'CUSTOMIZER' || tool.type === 'VIEWER' || tool.type === 'CONFIGURATOR_2') {
            setLastUsedTool(tool.type as EnvType);
        }
        window.location.href = tool.url;
    };

    const isAiActive = activeToolType === 'AI_STUDIO';

    return (
        <Container>
            <Trigger
                onClick={(e) => (isOpen ? closeDropdown() : openDropdown(e.currentTarget, 'bottom-start'))}
                data-testid='sidebar-solution-switcher'
                aria-haspopup='menu'
                aria-expanded={isOpen}
            >
                <TriggerIcon>
                    <Icon>{activeTool?.icon}</Icon>
                </TriggerIcon>
                {isAiActive ? <AiGradientLabel>{activeTool?.name}</AiGradientLabel> : <TriggerLabel>{activeTool?.name}</TriggerLabel>}
                <TriggerArrow $isOpen={isOpen}>
                    <AngleDown />
                </TriggerArrow>
            </Trigger>
            {isOpen && (
                <Dropdown width='360px' surfacePadding='0'>
                    <DropdownContent>
                        <DropdownList>
                            {tools.map((tool) => {
                                if (isWix && tool.type === 'CONFIGURATOR') return null;
                                return (
                                    <DropdownListItem key={tool.type} $active={tool.type === activeToolType} role='menuitem' onClick={() => handleSelect(tool)}>
                                        <DropdownItemIcon $size='large'>
                                            <Icon>{tool.icon}</Icon>
                                        </DropdownItemIcon>
                                        <DropdownItemBody>
                                            <DropdownItemText>
                                                {tool.type === 'AI_STUDIO' ? <AiGradientItemText>{tool.name}</AiGradientItemText> : tool.name}
                                                {tool.isAI && <AiTag>AI</AiTag>}
                                                {tool.isNew && <NewTag>New</NewTag>}
                                                {tool.isEarlyAccess && <EarlyAccessTag>Early Access</EarlyAccessTag>}
                                            </DropdownItemText>
                                            <DropdownItemCaption>{tool.description}</DropdownItemCaption>
                                        </DropdownItemBody>
                                    </DropdownListItem>
                                );
                            })}
                        </DropdownList>
                    </DropdownContent>
                </Dropdown>
            )}
        </Container>
    );
};

export default SolutionSwitcher;
